import { AxiosInstance, AxiosResponse } from 'axios';
import {
	CreateEventDto,
	DayOfWeekDto,
	EventDto,
	EventInstanceFilter,
	EventInstanceWithUserAttendanceDto,
	EventInvitationAnswerDto,
	EventParticipantsFilter,
	EventPeriodType,
	EventToUserDto,
	EventUsersInvitationDto,
	ListWithTotals,
	PrivacyTypeDto,
	ScheduleRepeatTypeDto,
	UpdateEventDto,
	EventInstanceDto,
	EventToUserOptions,
	UserParticipantShortDto,
	EventSitemap,
	IntakeFormUserDataResults,
	CitiesRsvpCountStatsResponse,
	EventCategoriesFilter,
	InterestDto,
	PublicJoinEventBody,
	EventTicketsFilter,
	EventTicketDto
} from '@sweatpals/shared';

export class EventsService {
	private readonly baseRoute = 'events';

	constructor(private readonly httpClient: AxiosInstance) {}

	public async resyncEventAnalytics(baseId: string, instanceDate: Date | string, authorId: string, periodFrom?: Date | string): Promise<AxiosResponse<void>> {
		return this.httpClient.post<void>(`${this.baseRoute}/sync-analytics`, {
			baseId,
			authorId,
			instance: instanceDate instanceof Date ? instanceDate.toISOString() : instanceDate.toString(),
			periodFrom: periodFrom instanceof Date ? periodFrom.toISOString() : periodFrom?.toString(),
		});
	}

	public async findEventsDatesByFilterPublic(filter: EventInstanceFilter): Promise<AxiosResponse<Date[]>> {
		return this.httpClient.post<Date[]>(`${this.baseRoute}/public/search/dates`, filter);
	}

	public async findByFilterPublic(filter: EventInstanceFilter): Promise<AxiosResponse<EventInstanceWithUserAttendanceDto[]>> {
		return this.httpClient.post<EventInstanceWithUserAttendanceDto[]>(`${this.baseRoute}/public/search`, filter);
	}

	public async findByFilterPublicMultiple(filter: EventInstanceFilter[]): Promise<AxiosResponse<EventInstanceWithUserAttendanceDto[][]>> {
		return this.httpClient.post<EventInstanceWithUserAttendanceDto[][]>(`${this.baseRoute}/public/search/multiple`, filter);
	}

	public async findByFilter(filter: EventInstanceFilter): Promise<AxiosResponse<EventInstanceWithUserAttendanceDto[]>> {
		return this.httpClient.post<EventInstanceWithUserAttendanceDto[]>(`${this.baseRoute}/search`, filter);
	}

	public async findByFilterMultiple(filter: EventInstanceFilter[]): Promise<AxiosResponse<EventInstanceWithUserAttendanceDto[][]>> {
		return this.httpClient.post<EventInstanceWithUserAttendanceDto[][]>(`${this.baseRoute}/search/multiple`, filter);
	}

	public async getPrivacyTypes(): Promise<AxiosResponse<PrivacyTypeDto[]>> {
		return this.httpClient.get<PrivacyTypeDto[]>(`${this.baseRoute}/privacy-types`);
	}

	public async getScheduleReportsTypes(): Promise<AxiosResponse<ScheduleRepeatTypeDto[]>> {
		return this.httpClient.get<ScheduleRepeatTypeDto[]>(`${this.baseRoute}/schedule-reports-type`);
	}

	public async getDaysOfWeek(): Promise<AxiosResponse<DayOfWeekDto[]>> {
		return this.httpClient.get<DayOfWeekDto[]>(`${this.baseRoute}/days-of-week`);
	}

	public async getById(eventId: string): Promise<AxiosResponse<EventDto>> {
		return this.httpClient.get<EventDto>(`${this.baseRoute}/${eventId}`);
	}

	public async getEventInstance(eventId: string, instance: Date | string): Promise<AxiosResponse<EventInstanceDto>> {
		return this.httpClient.get<EventInstanceDto>(`${this.baseRoute}/${eventId}/instance/${instance}`);
	}

	public async getPublicEventInstanceByAlias(eventAlias: string, shortLocalInstance: string): Promise<AxiosResponse<EventInstanceDto>> {
		return this.httpClient.get<EventInstanceDto>(`${this.baseRoute}/public/${eventAlias}/short-instance/${shortLocalInstance}`);
	}

	public async getPublicEventInstance(eventId: string, instance: Date | string): Promise<AxiosResponse<EventInstanceDto>> {
		return this.httpClient.get<EventInstanceDto>(`${this.baseRoute}/public/${eventId}/instance/${instance}`);
	}

	public async getEventByInvitationCode(invitationCode: string): Promise<AxiosResponse<EventInstanceDto>> {
		return this.httpClient.get<EventInstanceDto>(`${this.baseRoute}/event/by-invitation-code/${invitationCode}`);
	}

	public async getParticipants(
		eventId: string,
		filter: EventParticipantsFilter
	): Promise<AxiosResponse<ListWithTotals<EventToUserDto>>> {
		return this.httpClient.get<ListWithTotals<EventToUserDto>>(`${this.baseRoute}/${eventId}/participants`, {
			params: filter,
		});
	}

	public async getEventParticipantEmails(
		eventId: string,
		filter: EventParticipantsFilter
	): Promise<AxiosResponse<UserParticipantShortDto[]>> {
		return this.httpClient.get<UserParticipantShortDto[]>(`${this.baseRoute}/${eventId}/participants/emails`, {
			params: filter,
		});
	}

	public async getPastEventOrInstance(
		eventId: string,
		instance: Date | string
	): Promise<AxiosResponse<EventInstanceDto>> {
		return this.httpClient.get<EventInstanceDto>(`${this.baseRoute}/${eventId}/past-event-or-instance/${instance}`);
	}

	public async create(body: CreateEventDto): Promise<AxiosResponse<EventDto>> {
		return this.httpClient.post<EventDto>(`${this.baseRoute}/`, body, { timeout: 120 * 1000 });
	}

	public async update(eventId: string, body: UpdateEventDto): Promise<AxiosResponse<EventDto>> {
		return this.httpClient.put<EventDto>(`${this.baseRoute}/${eventId}`, body, { timeout: 120 * 1000 });
	}

	public async updateEventVerifyStatus(eventId: string, verifyStatus: boolean): Promise<AxiosResponse<boolean>> {
		return this.httpClient.put<boolean>(`${this.baseRoute}/${eventId}/verify-status`, { verifyStatus });
	}

	public async delete(
		eventId: string,
		query: { instance?: Date; type?: EventPeriodType } = {}
	): Promise<AxiosResponse<void>> {
		return this.httpClient.delete<void>(`${this.baseRoute}/${eventId}`, { params: query });
	}

	public async joinEvent(eventId: string, options: EventToUserOptions): Promise<AxiosResponse<EventToUserDto>> {
		return this.httpClient.post<EventToUserDto>(`${this.baseRoute}/${eventId}/join`, {}, {
			params: options
		});
	}

	public async publicJoinEvent(eventId: string, options: EventToUserOptions, body: PublicJoinEventBody): Promise<AxiosResponse<EventToUserDto>> {
		return this.httpClient.post<EventToUserDto>(`${this.baseRoute}/public/${eventId}/join`, body, {
			params: options
		});
	}

	public async joinEventWaitlist(eventId: string, instance: string | Date): Promise<AxiosResponse<EventToUserDto>> {
		return this.httpClient.post<EventToUserDto>(`${this.baseRoute}/${eventId}/join/waitlist/${instance}`);
	}

	public async inviteUsers(eventId: string, body: EventUsersInvitationDto): Promise<AxiosResponse<EventToUserDto[]>> {
		return this.httpClient.post<EventToUserDto[]>(`${this.baseRoute}/${eventId}/invite`, body);
	}

	public async answerInvitation(
		eventId: string,
		body: EventInvitationAnswerDto
	): Promise<AxiosResponse<EventToUserDto>> {
		return this.httpClient.put<EventToUserDto>(`${this.baseRoute}/${eventId}/invite/answer`, body);
	}

	public async leaveEvent(eventId: string): Promise<AxiosResponse<void>> {
		return this.httpClient.delete(`${this.baseRoute}/${eventId}/leave`);
	}

	public async getEventsSitemap(filter: EventInstanceFilter): Promise<AxiosResponse<EventSitemap[]>> {
		return this.httpClient.post<EventSitemap[]>(`${this.baseRoute}/sitemap`, filter);
	}

	public async getEventGroupChatInvitationCode(eventId: string): Promise<AxiosResponse<string>> {
		return this.httpClient.get<string>(`${this.baseRoute}/${eventId}/group-chat/invitation-code`);
	}

	public async getEventRsvpCount(filter: EventInstanceFilter): Promise<AxiosResponse<number>> {
		return this.httpClient.post<number>(`${this.baseRoute}/rsvp/count`, filter);
	}

	public async getCitiesRsvpCount(groupBy: 'createdAt' | 'instance'): Promise<AxiosResponse<CitiesRsvpCountStatsResponse>> {
		return this.httpClient.get<CitiesRsvpCountStatsResponse>(`${this.baseRoute}/rsvp/cities/count/month`, {
			params: {
				groupBy,
			},
		});
	}

	public async checkIntakePassed(eventId: string, instance: string): Promise<AxiosResponse<boolean>> {
		return this.httpClient.get<boolean>(`${this.baseRoute}/check-intake/${eventId}/instance/${instance}`);
	}

	public async singIntakeForm(eventId: string, instance: string, body: IntakeFormUserDataResults): Promise<AxiosResponse<boolean>> {
		return this.httpClient.put<boolean>(`${this.baseRoute}/sign-intake/${eventId}/instance/${instance}`, body);
	}

	public async getEventsCount(filter: EventInstanceFilter): Promise<AxiosResponse<number>> {
		return this.httpClient.post<number>(`${this.baseRoute}/events-count`, filter);
	}

	public async getEventsCountPublic(filter: EventInstanceFilter): Promise<AxiosResponse<number>> {
		return this.httpClient.post<number>(`${this.baseRoute}/events-count/public`, filter);
	}

	public async getEventsCategories(filter: EventCategoriesFilter): Promise<AxiosResponse<InterestDto[]>> {
		return this.httpClient.post<InterestDto[]>(`${this.baseRoute}/categories`, filter);
	}

	public async getTicketsListByUser(userId: string, query: EventTicketsFilter): Promise<AxiosResponse<ListWithTotals<EventTicketDto>>> {
		return this.httpClient.get<ListWithTotals<EventTicketDto>>(`${'payments'}/tickets/by-user/${userId}`, {
			params: query,
		});
	}

	public async updateFeedEditorPickCollectionName(feedName: string): Promise<AxiosResponse<void>> {
		return this.httpClient.post<void>(`${this.baseRoute}/feed/editor-pick-collection-name`, { feedName });
	}

	public async getEditorPickCollectionName(): Promise<AxiosResponse<{ message: string }>> {
		return this.httpClient.get<{ message: string }>(`${this.baseRoute}/feed/editor-pick-collection-name`);
	}
}

export default EventsService;
